<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ isEditMode ? "Edit Topic" : "Create New Topic" }}
      </p>
      <button type="button" class="delete" @click="close" aria-label="close" />
    </header>

    <section class="modal-card-body">
      <b-field
        label="Title"
        label-position="on-border"
        :type="titleError ? 'is-danger' : ''"
        :message="titleError"
      >
        <b-input
          ref="titleInput"
          v-model="formData.title"
          placeholder="Enter topic title"
          required
          @input="validateTitle"
        />
      </b-field>

      <b-field label="Description" label-position="on-border">
        <b-input
          v-model="formData.description"
          type="textarea"
          placeholder="Enter topic description"
          rows="3"
        />
      </b-field>

      <!-- Tags Field -->
      <b-field label="Tags" label-position="on-border">
        <b-taginput
          v-model="formData.tags"
          placeholder="Add a tag"
          :data="[]"
          autocomplete
          allow-new
          open-on-focus
          @typing="(tag) => tag.toLowerCase()"
        />
      </b-field>

      <!-- URLs Field -->
      <b-field label="Video URLs" label-position="on-border">
        <b-taginput
          v-model="formData.urls"
          placeholder="Add video URL"
          :data="[]"
          autocomplete
          allow-new
          open-on-focus
        />
      </b-field>

      <!-- Workbook Reference Field -->
      <b-field label="Workbook Reference" label-position="on-border">
        <b-input
          v-model="formData.workbookRef"
          placeholder="Enter workbook reference"
        />
      </b-field>

      <div class="is-flex is-justify-content-space-between">
        <b-field
          label="Difficulty Level"
          label-position="on-border"
          :type="difficultyError ? 'is-danger' : ''"
          :message="difficultyError"
        >
          <b-select
            v-model="formData.difficultyLevel"
            placeholder="Select difficulty"
            @input="validateDifficulty"
          >
            <option
              v-for="level in difficultyLevels"
              :key="level"
              :value="level"
            >
              {{ level }}
            </option>
          </b-select>
        </b-field>

        <b-field class="mx-2">
          <b-switch
            v-model="formData.isPublic"
            passive-type="is-warning"
            type="is-info"
            :disabled="isEditMode || !isAdmin"
          >
            {{ formData.isPublic ? "Public" : "Private" }}
          </b-switch>
          <p v-if="formData.owner" class="is-size-7 ml-4 has-text-grey">
            Created by {{ formData.owner.firstName }}
            {{ formData.owner.lastName }}
          </p>
        </b-field>

        <b-field
          label="Status"
          label-position="on-border"
          :type="statusError ? 'is-danger' : ''"
          :message="statusError"
        >
          <b-select
            v-model="formData.status"
            placeholder="Select status"
            @input="validateStatus"
          >
            <option
              v-for="status in statusOptions"
              :key="status"
              :value="status"
            >
              {{ status }}
            </option>
          </b-select>
        </b-field>
      </div>
    </section>

    <footer class="modal-card-foot is-flex is-justify-content-space-between">
      <b-button type="is-primary" @click="save" :disabled="!canSave">
        {{ isEditMode ? "Save Changes" : "Create Topic" }}
      </b-button>

      <b-button
        v-if="isEditMode && !formData.isPublic"
        type="is-warning"
        @click="submitToPublic"
        :loading="isSubmitting"
      >
        Clone to Public
      </b-button>

      <b-button @click="close">Cancel</b-button>
    </footer>
  </div>
</template>

<script>
import { TopicsService } from "@/services/CoachingServices";
import { TopicDifficulty, ContentStatus } from "vue-chess-shared";
import { mapGetters } from "vuex";

export default {
  name: "TopicEditor",

  props: {
    topic: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      formData: this.getDefaultFormData(),
      titleError: "",
      difficultyError: "",
      statusError: "",
      isSubmitting: false,
      difficultyLevels: Object.values(TopicDifficulty),
      statusOptions: Object.values(ContentStatus),
    };
  },

  computed: {
    ...mapGetters({
      profile: "getProfile",
      getUserId: "getUserId",
    }),

    isAdmin() {
      return this.profile?.isAdmin ?? false;
    },

    userId() {
      return `${this.getUserId}`;
    },

    currentOwner() {
      return {
        firstName: this.profile?.firstName || "",
        lastName: this.profile?.lastName || "",
      };
    },

    isEditMode() {
      return !!this.topic;
    },

    hasChanges() {
      if (!this.isEditMode) {
        return true;
      }

      // Remove ownership-related properties from change detection
      return (
        this.formData.title !== this.topic?.title ||
        this.formData.description !== this.topic?.description ||
        this.formData.difficultyLevel !== this.topic?.difficultyLevel ||
        this.formData.status !== this.topic?.status ||
        !this.areArraysEqual(this.formData.tags, this.topic?.tags) ||
        !this.areArraysEqual(this.formData.urls, this.topic?.urls) ||
        this.formData.workbookRef !== this.topic?.workbookRef
      );
    },

    isValid() {
      return Boolean(
        this.formData.title?.trim().length >= 3 &&
          this.formData.difficultyLevel &&
          this.formData.status &&
          !this.titleError &&
          !this.difficultyError &&
          !this.statusError
      );
    },

    canSave() {
      return this.isValid && this.hasChanges;
    },
  },

  watch: {
    topic: {
      immediate: true,
      handler() {
        this.formData = this.getDefaultFormData();
        this.validateAll();
      },
    },
  },

  methods: {
    getDefaultFormData() {
      if (this.isEditMode) {
        return {
          title: this.topic?.title || "",
          description: this.topic?.description || "",
          difficultyLevel:
            this.topic?.difficultyLevel || TopicDifficulty.Beginner,
          status: this.topic?.status || ContentStatus.Draft,
          isPublic: this.topic?.isPublic,
          ownerId: this.topic?.ownerId,
          owner: this.topic?.owner,
          tags: this.topic?.tags || [],
          urls: this.topic?.urls || [],
          workbookRef: this.topic?.workbookRef || "",
          originalTopicId: this.topic?.originalTopicId,
        };
      }

      // For new topics
      return {
        title: "",
        description: "",
        difficultyLevel: TopicDifficulty.Beginner,
        status: ContentStatus.Draft,
        isPublic: this.isAdmin,
        ownerId: this.userId,
        owner: this.currentOwner,
        tags: [],
        urls: [],
        workbookRef: "",
      };
    },

    validateTitle() {
      if (!this.formData.title?.trim()) {
        this.titleError = "Topic title is required";
      } else if (this.formData.title.length < 3) {
        this.titleError = "Topic title must be at least 3 characters";
      } else {
        this.titleError = "";
      }
    },

    validateDifficulty() {
      this.difficultyError = !this.formData.difficultyLevel
        ? "Please select a difficulty level"
        : "";
    },

    validateStatus() {
      this.statusError = !this.formData.status ? "Please select a status" : "";
    },

    validateAll() {
      this.validateTitle();
      this.validateDifficulty();
      this.validateStatus();
      return !this.titleError && !this.difficultyError && !this.statusError;
    },

    async save() {
      if (!this.validateAll()) {
        return;
      }

      try {
        let topic;
        if (this.isEditMode) {
          // Remove properties that shouldn't be updated
          const updateData = { ...this.formData };
          delete updateData.isPublic;
          delete updateData.ownerId;
          delete updateData.owner;

          topic = await TopicsService.updateTopic(this.topic.id, updateData);
          this.$buefy.toast.open({
            message: "Topic updated successfully",
            type: "is-success",
          });
        } else {
          topic = await TopicsService.createTopic(this.formData);

          this.$buefy.toast.open({
            message: "Topic created successfully",
            type: "is-success",
          });
        }

        this.$emit("saved", topic);
        this.close();
      } catch (error) {
        console.error("Error saving topic:", error);
        this.$buefy.toast.open({
          message: "Failed to save topic",
          type: "is-danger",
        });
      }
    },

    async submitToPublic() {
      if (!this.topic) return;

      try {
        this.isSubmitting = true;

        const publicTopic = await TopicsService.cloneToPublic(
          this.topic.id,
          this.userId,
          this.currentOwner
        );

        this.$buefy.toast.open({
          message: "Topic submitted for public review",
          type: "is-success",
          duration: 5000,
        });

        this.$emit("saved", publicTopic);
        this.close();
      } catch (error) {
        console.error("Error submitting to public:", error);
        this.$buefy.toast.open({
          message: "Failed to submit topic for public review",
          type: "is-danger",
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    resetForm() {
      this.formData = this.getDefaultFormData();
      this.titleError = "";
      this.difficultyError = "";
      this.statusError = "";
      this.isBulkMode = false;
      this.pgnText = "";
    },

    close() {
      this.resetForm();
      this.$emit("close");
    },

    areArraysEqual(arr1, arr2) {
      if (!arr1 || !arr2) return false;
      if (arr1.length !== arr2.length) return false;
      return arr1.every((item, index) => item === arr2[index]);
    },
  },

  mounted() {
    // Focus the title input when the component is mounted
    this.$nextTick(() => {
      this.$refs.titleInput?.focus();
    });
  },
};
</script>

<style scoped>
.pgn-textarea {
  font-family: monospace;
  font-size: 14px;
}

.modal-card {
  width: 640px; /* Increased width to accommodate new fields */
}
</style>
